import * as React from 'react';
import { useTheme } from '@mui/material/styles';

/**
 * An interactive constellation network rendered on <canvas>.
 * Nodes drift, link to nearby neighbours, and gravitate toward the cursor.
 * Lightweight, DPR-aware, and pauses when off-screen / reduced-motion.
 */
export default function ParticleField({ density = 0.00009, maxParticles = 130 }) {
  const canvasRef = React.useRef(null);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');

    const reduce =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let width = 0;
    let height = 0;
    let dpr = Math.min(window.devicePixelRatio || 1, 2);
    let particles = [];
    let raf = null;
    let running = true;
    const mouse = { x: -9999, y: -9999, active: false };

    const rand = (min, max) => Math.random() * (max - min) + min;

    const build = () => {
      const rect = canvas.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const count = Math.min(maxParticles, Math.floor(width * height * density));
      particles = new Array(count).fill(0).map(() => ({
        x: rand(0, width),
        y: rand(0, height),
        vx: rand(-0.35, 0.35),
        vy: rand(-0.35, 0.35),
        r: rand(1, 2.4),
      }));
    };

    const linkColor = isDark ? '120, 170, 255' : '60, 120, 220';
    const dotColor = isDark ? '150, 190, 255' : '70, 120, 210';

    const step = () => {
      if (!running) return;
      ctx.clearRect(0, 0, width, height);
      const linkDist = 130;

      for (let i = 0; i < particles.length; i += 1) {
        const p = particles[i];

        // cursor attraction
        if (mouse.active) {
          const dx = mouse.x - p.x;
          const dy = mouse.y - p.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < 26000) {
            const f = (26000 - d2) / 26000;
            p.vx += (dx / Math.sqrt(d2 + 0.01)) * f * 0.06;
            p.vy += (dy / Math.sqrt(d2 + 0.01)) * f * 0.06;
          }
        }

        p.x += p.vx;
        p.y += p.vy;
        p.vx *= 0.99;
        p.vy *= 0.99;

        // gentle constant drift
        p.vx += rand(-0.02, 0.02);
        p.vy += rand(-0.02, 0.02);

        // wrap edges
        if (p.x < -20) p.x = width + 20;
        if (p.x > width + 20) p.x = -20;
        if (p.y < -20) p.y = height + 20;
        if (p.y > height + 20) p.y = -20;

        // draw node
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${dotColor}, ${isDark ? 0.85 : 0.7})`;
        ctx.fill();

        // links
        for (let j = i + 1; j < particles.length; j += 1) {
          const q = particles[j];
          const dx = p.x - q.x;
          const dy = p.y - q.y;
          const dist = Math.hypot(dx, dy);
          if (dist < linkDist) {
            const a = (1 - dist / linkDist) * (isDark ? 0.5 : 0.35);
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(q.x, q.y);
            ctx.strokeStyle = `rgba(${linkColor}, ${a})`;
            ctx.lineWidth = 1;
            ctx.stroke();
          }
        }

        // link to cursor
        if (mouse.active) {
          const dist = Math.hypot(p.x - mouse.x, p.y - mouse.y);
          if (dist < 170) {
            const a = (1 - dist / 170) * 0.55;
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(mouse.x, mouse.y);
            ctx.strokeStyle = `rgba(${linkColor}, ${a})`;
            ctx.lineWidth = 1;
            ctx.stroke();
          }
        }
      }

      raf = requestAnimationFrame(step);
    };

    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouse.x = e.clientX - rect.left;
      mouse.y = e.clientY - rect.top;
      mouse.active = true;
    };
    const onLeave = () => {
      mouse.active = false;
      mouse.x = -9999;
      mouse.y = -9999;
    };

    const onResize = () => {
      build();
    };

    // Static single frame for reduced motion
    build();
    if (reduce) {
      const drawStatic = () => {
        ctx.clearRect(0, 0, width, height);
        particles.forEach((p) => {
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${dotColor}, 0.6)`;
          ctx.fill();
        });
      };
      drawStatic();
    } else {
      raf = requestAnimationFrame(step);
    }

    // Pause when tab hidden
    const onVisibility = () => {
      running = !document.hidden;
      if (running && !reduce) {
        raf = requestAnimationFrame(step);
      } else if (raf) {
        cancelAnimationFrame(raf);
      }
    };

    window.addEventListener('resize', onResize);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerdown', onMove);
    document.addEventListener('visibilitychange', onVisibility);
    canvas.parentElement.addEventListener('pointerleave', onLeave);

    return () => {
      running = false;
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerdown', onMove);
      document.removeEventListener('visibilitychange', onVisibility);
      if (canvas.parentElement) {
        canvas.parentElement.removeEventListener('pointerleave', onLeave);
      }
    };
  }, [isDark, density, maxParticles]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        display: 'block',
      }}
      aria-hidden="true"
    />
  );
}
